import { withErrorBoundary } from 'app/components/error-boundary';
import { useTutorials } from 'app/components/tutorial-overlay';
import { images } from 'app/design/images';
import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Checkbox } from 'app/design/primitives/checkbox';
import { PCImage } from 'app/design/primitives/image';
import { usePagePadding } from 'app/design/primitives/page';
import { RemoteError } from 'app/design/primitives/remote-error';
import { Text } from 'app/design/primitives/text';

import { useDeviceHeight, useDeviceWidth } from 'app/hooks/use-device-size';
import { useQuickOffers } from 'app/hooks/use-quickoffers';
import { Event, track } from 'app/lib/analytics';
import { getI18nLanguage } from 'app/lib/language';
import { useAllAddresses } from 'app/network/address';
import { useAllGroups } from 'app/network/groups';
import { useCreateQuickListings } from 'app/network/listing';
import { popularItemSchema, type PopularItem } from 'app/types/category';
import type { CreateListingDTO } from 'app/types/listing';
import { useTrialFreeSubAvailable } from 'app/network/promotions';
import { hasSignal, Signal } from 'app/network/signals';
import { ListingSubtype, ListingType } from 'app/types/listing';
import type { NavigationScreen } from 'app/types/navigation';
import { useUISize, usePageTitle } from 'app/utils/web-helpers';
import { useEffect, useState } from 'react';
import { Controller, FormProvider, useForm, type ControllerRenderProps, type FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Pressable, View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import { GroupsSection } from '../new/components/groups-section';
import { createTheme as createDSTheme } from 'app/shopify';
import { usePresentAppDownloadPrompt } from 'app/hooks/use-present-app-download-prompt';
const theme = createDSTheme();

const ConfirmWebScreenComponent: NavigationScreen<'confirm'> = ({ navigation, route }) => {
  const { t } = useTranslation('confirm');
  const { t: tAPI } = useTranslation('api-errors');
  const i18nLanguage = getI18nLanguage();
  const groupsQuery = useAllGroups(true);
  const addressesQuery = useAllAddresses();
  const presentAppDownloadPrompt = usePresentAppDownloadPrompt();

  const setPageTitle = usePageTitle();
  useEffect(() => setPageTitle(`${t('headerTitle')} | ${t('headerTitle', { ns: 'confirm' })}`), [setPageTitle, t]);

  interface FormSchema {
    addressIds?: string[];
    groupIds?: string[];
    // Add dynamic keys for each selected item
    [key: `row_${string}`]: boolean;
  }

  const formFields = useForm<FormSchema>({
    mode: 'onChange',
    defaultValues: {
      addressIds: addressesQuery.data?.map((a) => a.id) ?? [],
      groupIds: groupsQuery.data?.map((g) => g.id) ?? [],
    },
  });
  const { control, handleSubmit } = formFields;

  const [selectedItems, setSelectedItems] = useState(() => {
    return route.params.selectedItems.map((popItem) => {
      return popularItemSchema.cast({
        ...popItem,
        selected: true,
      });
    });
  });

  const numSelectedItems = selectedItems.filter((item) => item.selected).length;
  const formIsValid = numSelectedItems > 0;

  // Toggle (on/off) an item
  const toggleSelectedItem = (popItem: PopularItem, field: ControllerRenderProps<FieldValues, `row_${string}`>) => {
    const selected = !popItem.selected;
    setSelectedItems((items) => items.map((item) => (item.id === popItem.id ? { ...item, selected } : item)));
    field.onChange(selected);
  };

  const { continueTutorial, postponeSignal } = useTutorials();

  const trialFreeSubAvailable = useTrialFreeSubAvailable(navigation);

  // Crete quick offers on API
  const {
    error: mutationError,
    mutate,
    isPending,
  } = useCreateQuickListings({
    async onSuccess(listings) {
      const stillSelectedItems = selectedItems.filter((item) => item.selected);
      track(Event.QuickOffersPosted, {
        num_items: stillSelectedItems.length,
        items: stillSelectedItems,
      });

      const listing = listings[0];

      const { shouldShowFreeSubScreen, navigateToFreeSubScreen, freeSubReceived } = trialFreeSubAvailable(listing);

      const userNowContributor = hasSignal(listing, Signal.UserLevelNowContributor);

      if (await continueTutorial({ numListingsPublished: stillSelectedItems.length })) {
        // Postpone signals until end of tutorial
        if (userNowContributor) {
          postponeSignal(Signal.UserLevelNowContributor);
        }
        if (freeSubReceived) {
          postponeSignal(Signal.TriggeredFreeSub);
        }
        return;
      }

      if (shouldShowFreeSubScreen) {
        navigateToFreeSubScreen();
      } else {
        // Continue to the next screen
        goToNext({ userNowContributor, listingIds: listings.map((l) => l.id) });
      }
      presentAppDownloadPrompt({ context: 'listing-published' });
    },
  });

  const { goToNext, goToPrevious } = useQuickOffers();

  const onGoingBack = () => {
    goToPrevious();
  };

  const onSubmit = handleSubmit((form) => {
    const groupIds = form.groupIds;

    // If the user is in a group, they saw the <GroupsSection> component to select where to post the listing(s), and they could, in theory, select neither group nor neighbourhood.
    // Otherwise (when !userIsInGroups), then we need to send the user's address in addressIds, because they didn't see the <GroupsSection> component.
    const userIsInGroup = (groupsQuery.data?.length ?? 0) > 0;
    const addressIds =
      (form.addressIds?.length ?? 0) > 0 ? form.addressIds : userIsInGroup ? [] : addressesQuery.data!.map((a) => a.id);

    const payload = selectedItems.flatMap<CreateListingDTO>((popItem) =>
      popItem.selected
        ? [
            {
              item: { name: popItem[i18nLanguage] },
              type: ListingType.offer,
              subtype: ListingSubtype.object,
              categoryPrimaryId: popItem.categoryId,
              serviceCategoryId: null,
              groupIds: groupIds!,
              addressIds: addressIds!,
              description: null,
              start: null,
              end: null,
              picture: null,
              pictureUrl: popItem.pictureUrl,
            },
          ]
        : [],
    );

    mutate({ listings: payload });
  });

  // Margin left and right of the pages
  const pagePadding = usePagePadding();
  const windowWidth = useDeviceWidth();
  const deviceHeight = useDeviceHeight();
  const itemWidth = windowWidth - pagePadding.paddingLeft - pagePadding.paddingRight;
  const { isLargeUI } = useUISize();

  return (
    <Row backgroundColor="surface.default" flex={1}>
      <Column flex={1} justifyContent="center">
        <View
          style={{
            marginRight: theme.spacing['spacing.general.x12'] + 20,
            marginLeft: theme.spacing['spacing.general.x12'] + 20,
          }}
        >
          <ScrollView
            showsVerticalScrollIndicator
            contentContainerStyle={{
              paddingBottom: theme.spacing['spacing.general.x8'],
              maxHeight: deviceHeight * 0.7,
            }}
          >
            {mutationError ? (
              <View style={{ marginHorizontal: 20 }}>
                <RemoteError
                  {...{
                    error: mutationError,
                    message: tAPI(mutationError.message),
                  }}
                />
              </View>
            ) : null}

            <Text
              fontWeight="700"
              marginBottom="spacing.general.x2"
              style={{ marginHorizontal: 16 + 30 }}
              textAlign="center"
              variant="headerXSmall"
            >
              {t('headerTitle')}
            </Text>

            <Text
              marginBottom="spacing.general.x2"
              style={{ marginHorizontal: 16 + 30 }}
              textAlign="center"
              variant="bodyLarge"
            >
              {t('text')}
            </Text>

            <View
              style={[
                {
                  borderRadius: theme.borderRadii['radius.xl'],
                  paddingVertical: 20,
                },
                isLargeUI && { width: itemWidth },
              ]}
            >
              <Column>
                {selectedItems.map((popItem: PopularItem, index: number) => (
                  <Controller
                    {...{ control }}
                    key={popItem.id}
                    name={`row_${index.toString()}`}
                    render={({ field }) => (
                      <Pressable onPress={() => toggleSelectedItem(popItem, field)}>
                        <Row
                          alignItems="center"
                          backgroundColor="surface.inverse.tertiary-neutral"
                          borderRadius="radius.lg"
                          height={48}
                          justifyContent="space-between"
                          marginVertical="spacing.general.x1"
                          paddingHorizontal="spacing.general.x5"
                          paddingVertical="spacing.general.x1"
                        >
                          <Text fontWeight={popItem.selected ? '700' : '500'} numberOfLines={1}>
                            {popItem[i18nLanguage]}
                          </Text>
                          <Checkbox checked={popItem.selected} onPress={() => toggleSelectedItem(popItem, field)} />
                        </Row>
                      </Pressable>
                    )}
                  />
                ))}
              </Column>
            </View>

            <FormProvider {...formFields}>
              <View>
                <GroupsSection />
              </View>
            </FormProvider>
          </ScrollView>
          <Row>
            <Button
              flex={1}
              marginHorizontal="spacing.general.x4"
              onPress={() => onGoingBack()}
              loading={isPending}
              variant="whiteWithBorder"
            >
              <Text color="text.primary" fontWeight={formIsValid ? '700' : '500'} textAlign="center">
                {t('previousStep')}
              </Text>
            </Button>

            <Button
              flex={1}
              marginHorizontal="spacing.general.x4"
              onPress={() => void onSubmit()}
              loading={isPending}
              variant="primary"
            >
              <Text color="surface.default" fontWeight={formIsValid ? '700' : '500'} textAlign="center">
                {t('publish', { num: numSelectedItems })}
              </Text>
            </Button>
          </Row>
        </View>
      </Column>
      <Column flex={1}>
        <PCImage flex={1} cachePolicy="memory-disk" source={images.web.your_selection} />
      </Column>
    </Row>
  );
};

export const ConfirmWebScreen: NavigationScreen<'confirm'> = withErrorBoundary(ConfirmWebScreenComponent);
